import { supabase } from '../lib/supabase';
import { myReaction, nextReaction, withMyReaction } from '../lib/reactions';
import type { Message } from '../types';

/**
 * Sets the signed-in user's own reaction on message `index` of a conversation
 * (set_my_message_reaction) and shows it straight away. Choosing the reaction you
 * already have removes it. If the server refuses, the screen goes back to what it was.
 *
 * `conversationId` may be missing for a chat with no saved conversation yet; the
 * reaction then only changes the screen.
 */
export function useChatReactions(
  conversationId: string | null | undefined,
  userId: string | undefined,
  setMessages: (updater: (prev: Message[]) => Message[]) => void,
) {
  async function react(index: number, picked: string) {
    if (!userId) return;
    let before: Message | undefined;
    let next: string | undefined;
    setMessages((prev) => {
      const target = prev[index];
      if (!target) return prev;
      before = target;
      next = nextReaction(myReaction(target, userId), picked);
      const updated = [...prev];
      updated[index] = withMyReaction(target, userId, next);
      return updated;
    });

    if (!conversationId) return;
    const { error } = await supabase.rpc('set_my_message_reaction', {
      p_conv_id: conversationId,
      p_index: index,
      p_reaction: next ?? null,
    });
    if (error) {
      console.error('set_my_message_reaction failed:', error);
      const restore = before;
      if (restore) setMessages((prev) => (prev[index] ? prev.map((m, i) => (i === index ? restore : m)) : prev));
    }
  }

  return { react };
}
